import { toast } from 'sonner';
import { useDispatch, useSelector } from 'react-redux';
import agentData from '../../../../../../constants/agents/initialStates/agentData';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAgentState } from '../../../../../../constants/agents/initialStates/agentState';
import {
  useCreateQualificationAgentV1Mutation,
  useUpdateQualificationAgentV1Mutation,
} from '../../../../../../services/api';
import {
  setAgentData,
  setAgentEtc,
  setAIAgentData,
  setPrevAgentStep,
  setQualificationAgentData,
  setSmartReplyData,
} from '../../../../../../redux/slices/agents/agentSlice';
import useEventScope from '../../../../../events/controllers/useEventScope';
import { getEventContextPath } from '../../../../../events/controllers/eventFormatters';
import {
  withEventScopeForCreate,
  withEventScopeForUpdate,
} from '../../../../../events/controllers/eventScopePayload';

const useQualificationHandler = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const qualification = useSelector(state => state?.agent?.qualificationAgent);
  const creationType = useSelector(state => state.agent.etc.creationType);
  const brandingPrimaryColor = useSelector(state => state.branding.branding.primaryColor);
  const search = useLocation().search;
  const redirect = new URLSearchParams(search).get('redirect');
  const _id = new URLSearchParams(search).get('_id');
  const { isEventContext, eventId, workflowId, workflowKey } = useEventScope();

  const [_CREATE_QUALIFICATION_V1, { isLoading: isCreatingV1 }] =
    useCreateQualificationAgentV1Mutation();
  const [_UPDATE_QUALIFICATION_V1, { isLoading: isUpdatingV1 }] =
    useUpdateQualificationAgentV1Mutation();

  const onPrevHandler = () => {
    dispatch(setPrevAgentStep());
  };

  const prepareAgentData = () => {
    const { funnel, ...rest } = qualification;

    return {
      ...rest,
    };
  };

  const onSubmitHandler = () => {
    switch (creationType) {
      case 'create':
        return createAgent();
      case 'edit':
        return editAgent();
      default:
        return createAgent();
    }
  };

  const createAgent = async () => {
    try {
      const payload = withEventScopeForCreate(prepareAgentData(), {
        isEventContext,
        eventId,
        workflowId,
      });
      const response = await _CREATE_QUALIFICATION_V1(payload);

      if (response?.data?.success) {
        toast.success('Agent created');

        clearAgentData();
        if (redirect === 'overview') {
          navigate(`${agentData?.overviewPath}?_id=${_id}&isQualification=true`);
        } else {
          navigate(
            isEventContext ? getEventContextPath(eventId, workflowKey) : agentData?.dashboardPath
          );
        }
      }

      if (response?.error) {
        toast.error(
          response?.error?.data?.errorObject?.userErrorText || "Couldn't create the agent"
        );
      }
    } catch (error) {
      toast.error("Couldn't create the agent");
    }
  };

  const editAgent = async () => {
    try {
      const body = withEventScopeForUpdate(
        prepareAgentData(),
        { isEventContext, eventId, workflowId },
        { eventId: qualification?.eventId, workflowId: qualification?.workflowId }
      );
      const response = await _UPDATE_QUALIFICATION_V1({ agentId: _id, body });

      if (response?.data?.success) {
        toast.success('Changes saved');

        clearAgentData();
        if (redirect === 'overview') {
          navigate(`${agentData?.overviewPath}?_id=${_id}&isQualification=true`);
        } else {
          navigate(
            isEventContext ? getEventContextPath(eventId, workflowKey) : agentData?.dashboardPath
          );
        }
      }

      if (response?.error) {
        toast.error(
          response?.error?.data?.errorObject?.userErrorText || "Couldn't save your changes"
        );
      }
    } catch (error) {
      toast.error("Couldn't save your changes");
    }
  };

  const clearAgentData = () => {
    const agentState = getAgentState(brandingPrimaryColor);
    dispatch(setAgentEtc({ ...agentState.etc, creationType: 'create' }));
    dispatch(setAgentData(agentState.data));
    dispatch(setAIAgentData(agentState.aiAgent));
    dispatch(setSmartReplyData(agentState.smartReply));
    dispatch(setQualificationAgentData(agentState.qualificationAgent));
  };

  return { onPrevHandler, onSubmitHandler, isCreatingV1, isUpdatingV1 };
};

export default useQualificationHandler;
